import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { api, PLAN_META, formatXof } from "../api";
import { clearReferralCode } from "../hooks/useReferralCapture";
import PaymentProgress from "../components/PaymentProgress";
import RedirectingOverlay from "../components/RedirectingOverlay";
import {
  CheckCircle2,
  Copy,
  CopyCheck,
  AlertTriangle,
  Clock,
  Mail,
  FileArchive,
  ShieldCheck,
  ArrowLeft,
  Coins,
  CreditCard,
  CircleDollarSign,
} from "lucide-react";

const USDT_ADDRESS = "TRfiTGAggy23LEBEhFEBp3Cx1Z4fHe8tMy";
const STORAGE_KEY = "dtsp_pending_order";

import { Smartphone } from "lucide-react";

const METHOD_ICON = {
  usdt: Coins,
  cinetpay: CreditCard,
  mobilemoney: Smartphone,
  cards: CreditCard,
  paypal: CircleDollarSign,
};

const METHOD_LABEL = {
  usdt: "checkout.payment_usdt",
  cinetpay: "checkout.payment_cinetpay",
  paypal: "checkout.payment_paypal",
};

const UI_METHOD_LABEL = {
  mobilemoney: "Mobile Money",
  cards: "Cartes bancaires",
};

export default function CheckoutPayment() {
  const { planId } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const meta = PLAN_META[planId];
  const tier = meta?.tier;

  // Hydrate from sessionStorage (filled at Step 1)
  const stored = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return obj?.plan_id === planId ? obj : null;
    } catch {
      return null;
    }
  }, [planId]);

  // Redirect to Step 1 if no data yet
  useEffect(() => {
    if (meta && !stored) {
      navigate(`/checkout/${planId}`, { replace: true });
    }
  }, [meta, stored, planId, navigate]);

  const [reference, setReference] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [progressStatus, setProgressStatus] = useState("idle");
  const [progressMsg, setProgressMsg] = useState("");

  // Reflect hash field state on the progress bar (idle → received).
  // Must be declared BEFORE any early return to keep hooks order stable.
  useEffect(() => {
    if (success || progressStatus === "checking" || progressStatus === "ok" || progressStatus === "error") return;
    const ref = reference.trim();
    const m = stored?.payment_method || "usdt";
    if (!ref) {
      setProgressStatus("idle");
      setProgressMsg("");
      return;
    }
    if (m === "usdt" && /^[a-fA-F0-9]{64}$/.test(ref)) {
      setProgressStatus("received");
      setProgressMsg("Hash valide détecté. Cliquez sur « Valider ma commande » pour lancer la vérification on-chain.");
    } else if (m !== "usdt" && ref.length >= 4) {
      setProgressStatus("received");
      setProgressMsg("Référence enregistrée. Cliquez sur « Valider ma commande » pour soumettre.");
    } else {
      setProgressStatus("idle");
      setProgressMsg("");
    }
  }, [reference, stored, progressStatus, success]);

  if (!meta) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="page-checkout-payment-404">
        <h1 className="text-3xl font-bold mb-4">Plan introuvable</h1>
        <Link to="/pricing" className="btn-primary inline-block mt-4">← Voir les tarifs</Link>
      </div>
    );
  }

  if (!stored) {
    // useEffect will navigate; render nothing in the meantime.
    return null;
  }

  const labelDuration = {
    "1m": t("pricing.duration_1m"),
    "3m": t("pricing.duration_3m"),
    "6m": t("pricing.duration_6m"),
    "12m": t("pricing.duration_12m"),
  };

  const method = stored.payment_method || "usdt";
  const uiMethod = stored.ui_method || method;
  const displayedMethodLabel = UI_METHOD_LABEL[uiMethod] || t(METHOD_LABEL[method] || "checkout.payment_usdt");
  const MethodIcon = METHOD_ICON[uiMethod] || METHOD_ICON[method] || Coins;

  const copyUsdt = () => {
    navigator.clipboard.writeText(USDT_ADDRESS).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const validate = () => {
    if (method === "cinetpay") return null; // pas de référence à saisir : redirection automatique
    const ref = reference.trim();
    if (!ref) return t("checkout.payment_reference") + " ?";
    if (method === "usdt") {
      if (!/^[a-fA-F0-9]{64}$/.test(ref)) return t("checkout.usdt_hash_format");
    }
    return null;
  };

  const submit = async () => {
    setError("");
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setProgressStatus("checking");
    setProgressMsg(
      method === "usdt"
        ? "Vérification de la transaction sur la blockchain Tron en cours… (5 à 30 s)"
        : method === "cinetpay"
        ? "Initialisation de votre paiement CinetPay…"
        : "Enregistrement de votre commande…"
    );
    try {
      const payload = {
        plan_id: planId,
        full_name: stored.full_name,
        email: stored.email,
        phone: stored.phone,
        broker: stored.broker || "",
        mt5_accounts: stored.mt5_accounts || [],
        payment_method: method,
        payment_reference: method === "cinetpay" ? "pending-cinetpay" : reference.trim(),
        referral_code: (stored.referral_code || "").toUpperCase(),
        lang,
      };
      const { data } = await api.post("/orders/submit", payload);

      // CinetPay: redirect customer to hosted checkout
      if (method === "cinetpay") {
        try {
          const fd = new FormData();
          fd.append("order_id", data.order_id);
          if (stored.cinetpay_channel) fd.append("channels", stored.cinetpay_channel);
          const init = await api.post("/payments/cinetpay/initialize", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (init.data?.payment_url) {
            setProgressStatus("ok");
            setProgressMsg("Redirection vers CinetPay…");
            sessionStorage.removeItem(STORAGE_KEY);
            clearReferralCode();
            window.location.href = init.data.payment_url;
            return;
          }
          throw new Error("CinetPay payment_url manquant");
        } catch (initErr) {
          const detail = initErr?.response?.data?.detail || "Échec d'initialisation CinetPay";
          setProgressStatus("error");
          setProgressMsg(detail);
          setError(detail);
          setSubmitting(false);
          return;
        }
      }

      setProgressStatus("ok");
      setProgressMsg(
        data.payment_verified
          ? `Paiement confirmé on-chain (${data.payment_amount_usdt ?? "?"} USDT). Commande #${data.order_id.slice(0, 8)} enregistrée.`
          : `Commande #${data.order_id.slice(0, 8)} enregistrée — vérification manuelle par notre équipe.`
      );
      setSuccess(data);
      sessionStorage.removeItem(STORAGE_KEY);
      clearReferralCode();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail || t("checkout.error_network");
      setProgressStatus("error");
      setProgressMsg(detail);
      setError(detail);
    } finally {
      if (method !== "cinetpay") setSubmitting(false);
    }
  };

  // SUCCESS VIEW
  if (success) {
    const msg = t("checkout.success_message")
      .replace("{name}", stored.full_name)
      .replace("{id}", success.order_id.slice(0, 8))
      .replace("{email}", stored.email);
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="checkout-success">
        <div className="glass rounded-3xl p-10 text-center border-emerald-400/40">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-400/10 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">{t("checkout.success_title")}</h1>
          <p className="text-zinc-300 leading-relaxed mb-8">{msg}</p>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 text-left mb-8">
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">
              {t("checkout.success_next_steps")}
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-zinc-300">
                <ShieldCheck className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                {t("checkout.success_step1")}
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-300">
                <FileArchive className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                {t("checkout.success_step2")}
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-300">
                <Mail className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                {t("checkout.success_step3")}
              </li>
            </ul>
          </div>
          <Link to="/" className="btn-primary inline-block mr-2" data-testid="success-home-link">
            {t("checkout.success_back_home")}
          </Link>
          <Link
            to={`/track/${success.order_id}`}
            className="btn-ghost inline-block px-4 py-2.5 rounded-lg text-sm"
            data-testid="success-track-link"
          >
            Suivre ma commande →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="page-checkout-payment">
      {/* Stepper */}
      <div className="mb-6 flex items-center gap-3 text-xs font-mono">
        <span className="text-zinc-500">1. {t("checkout.step_info")}</span>
        <span className="text-zinc-600">→</span>
        <span className="text-cyan-300 font-semibold">2. {t("checkout.step_payment")}</span>
      </div>

      <div className="flex items-center justify-between mb-8 gap-4">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter">
          {t("checkout.payment_instructions")}
        </h1>
        <Link
          to={`/checkout/${planId}`}
          className="shrink-0 text-xs text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1.5"
          data-testid="checkout-back-to-info"
        >
          <ArrowLeft className="h-4 w-4" /> {t("checkout.back_to_info")}
        </Link>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        {/* LEFT */}
        <div className="space-y-6">
          {/* Selected method recap */}
          <section className="glass rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-400/10 text-cyan-300 flex items-center justify-center">
              <MethodIcon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-0.5">
                {t("checkout.method")}
              </div>
              <div className="text-sm font-semibold">{displayedMethodLabel}</div>
            </div>
          </section>

          {/* USDT instructions */}
          {method === "usdt" && (
            <section className="glass rounded-2xl p-6 space-y-5">
              <h2 className="text-lg font-semibold">{t("checkout.payment_instructions")}</h2>

              <div className="flex flex-col sm:flex-row gap-5 items-start">
                <div className="bg-white p-2 rounded-lg shrink-0">
                  <img
                    src="/assets/usdt_trc20_qr.png"
                    alt="USDT TRC20 QR"
                    className="w-36 h-36 object-contain"
                  />
                </div>
                <div className="flex-1 w-full space-y-3">
                  <div>
                    <div className="text-xs text-zinc-400 mb-1">{t("checkout.usdt_amount")}</div>
                    <div className="text-2xl font-mono font-semibold text-amber-300">
                      ≈ {meta.usd} USDT
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400 mb-1">{t("checkout.usdt_wallet")}</div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono bg-black/30 px-3 py-2 rounded border border-white/[0.06] flex-1 overflow-x-auto">
                        {USDT_ADDRESS}
                      </code>
                      <button type="button" onClick={copyUsdt}
                        className="shrink-0 px-3 py-2 rounded bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20 text-cyan-300 text-xs flex items-center gap-1.5"
                        data-testid="checkout-copy-usdt">
                        {copied ? (
                          <><CopyCheck className="h-4 w-4" /> {t("checkout.usdt_copied")}</>
                        ) : (
                          <><Copy className="h-4 w-4" /> {t("checkout.usdt_copy")}</>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-amber-300 bg-amber-400/5 border border-amber-400/20 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{t("checkout.usdt_note")}</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* CinetPay info */}
          {method === "cinetpay" && (
            <section className="glass rounded-2xl p-6 space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-cyan-300" />
                Paiement sécurisé CinetPay
              </h2>
              <p className="text-sm text-zinc-300 leading-relaxed">
                En cliquant sur <b>« Confirmer la commande »</b>, vous serez redirigé vers la page de paiement
                officielle CinetPay. Vous pourrez payer avec <b>Mobile Money</b> (Orange, MTN, Moov, Wave),
                <b> carte bancaire</b> Visa/Mastercard ou <b>autres moyens locaux</b>.
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Une fois le paiement confirmé par CinetPay, votre commande passe automatiquement en
                statut « payée ». Aucune action manuelle n'est nécessaire.
              </p>
            </section>
          )}

          {/* Reference / Hash field — masqué pour CinetPay (auto-géré par redirection) */}
          {method !== "cinetpay" && (
            <section className="glass rounded-2xl p-6">
              <label className="block text-xs text-zinc-400 mb-1.5">
                {t("checkout.payment_reference")} *
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={
                  method === "usdt"
                    ? "ex: a3f7c9b2e1d8…  (TXID — 64 caractères)"
                    : t("checkout.payment_reference_help")
                }
                className="input font-mono text-xs"
                data-testid="checkout-payment-reference"
              />
              {method === "usdt" && (
                <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                  {t("checkout.usdt_hash_help")}
                </p>
              )}
            </section>
          )}

          {/* Payment progress / redirect indicator */}
          {method === "cinetpay" ? (
            <RedirectingOverlay
              status={progressStatus}
              message={progressMsg}
              brand={uiMethod === "cards" ? "la page de paiement carte" : uiMethod === "mobilemoney" ? "Mobile Money" : "CinetPay"}
            />
          ) : (
            <PaymentProgress status={progressStatus} message={progressMsg} />
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <button onClick={submit} disabled={submitting}
            className="btn-primary w-full py-4 text-sm font-semibold disabled:opacity-60 disabled:cursor-wait"
            data-testid="checkout-submit">
            {submitting ? t("checkout.submitting") : t("checkout.submit_order")}
          </button>
        </div>

        {/* RIGHT — Summary */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="glass rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">
              {t("checkout.summary")}
            </div>
            <div className="text-sm text-zinc-400 mb-1">{t(`pricing.${tier}`)}</div>
            <div className="text-xl font-semibold mb-3">{labelDuration[meta.duration]}</div>
            <div className="flex items-baseline gap-1 mb-1">
              <div className="text-3xl font-bold tracking-tighter font-mono">
                {formatXof(meta.xof)}
              </div>
              <div className="text-sm text-zinc-400">{t("common.currency")}</div>
            </div>
            <div className="text-xs text-zinc-500 mb-5">
              ≈ ${meta.usd.toLocaleString("en-US")} USD
            </div>

            <div className="border-t border-white/[0.06] pt-4 mt-4 space-y-1.5 text-xs">
              <div className="text-zinc-500 uppercase tracking-[0.12em] mb-1">
                {t("checkout.step_info")}
              </div>
              <div className="text-zinc-300 truncate">{stored.full_name}</div>
              <div className="text-zinc-400 truncate">{stored.email}</div>
              <div className="text-zinc-400 truncate">{stored.phone}</div>
              {stored.broker ? <div className="text-zinc-500 truncate">{stored.broker}</div> : null}
              {(stored.mt5_accounts || []).length > 0 && (
                <div className="text-zinc-500 truncate">
                  MT5: {stored.mt5_accounts.join(", ")}
                </div>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-2 text-xs text-zinc-400">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_time")}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_email")}
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_license")}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
